import { useCallback, useMemo } from "react";
import type { FilterGroup } from "@/lib/filters";
import { EMPTY_GROUP } from "@/lib/filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Storage version for schema compatibility
const STORAGE_VERSION = "v1";

interface AccountRelationFiltersState {
  opportunity: FilterGroup;
  touchpoint: FilterGroup;
}

const defaultState: AccountRelationFiltersState = {
  opportunity: EMPTY_GROUP,
  touchpoint: EMPTY_GROUP,
};

/**
 * Hook to manage account relation filter state for Opportunity and Touchpoint objects.
 *
 * - Persists state to sessionStorage for tab-specific isolation.
 * - Provides setters and clear helpers for each individual object type.
 * - Used for filtering accounts based on attributes of related entities.
 */
export function useAccountFilterState(scope: string) {
  // Use sessionStorage for tab-specific filter isolation
  const storageKey = `${scope}::relation-filters::${STORAGE_VERSION}`;

  // Persistence layer
  const [state, setState] =
    useTrackedSessionStorage<AccountRelationFiltersState>(
      storageKey,
      defaultState,
    );

  // Update helpers
  const updateFilterState = useCallback(
    (updates: Partial<AccountRelationFiltersState>) => {
      setState((prev) => ({
        opportunity: updates.opportunity ?? prev?.opportunity ?? EMPTY_GROUP,
        touchpoint: updates.touchpoint ?? prev?.touchpoint ?? EMPTY_GROUP,
      }));
    },
    [setState],
  );

  const setOpportunityFilters = useCallback(
    (filters: FilterGroup) => updateFilterState({ opportunity: filters }),
    [updateFilterState],
  );

  const setTouchpointFilters = useCallback(
    (filters: FilterGroup) => updateFilterState({ touchpoint: filters }),
    [updateFilterState],
  );

  const clearAllFilters = useCallback(() => {
    setState({
      opportunity: EMPTY_GROUP,
      touchpoint: EMPTY_GROUP,
    });
  }, [setState]);

  // Quick flags for UI badges
  const hasOpportunityFilters = useMemo(
    () => state?.opportunity?.filters?.length > 0,
    [state.opportunity],
  );
  const hasTouchpointFilters = useMemo(
    () => state?.touchpoint?.filters?.length > 0,
    [state.touchpoint],
  );

  return {
    opportunityFilters: state?.opportunity ?? EMPTY_GROUP,
    touchpointFilters: state?.touchpoint ?? EMPTY_GROUP,
    setOpportunityFilters,
    setTouchpointFilters,
    clearAllFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
  };
}
